// Minimal typings for the parts of the Google Maps JS API (places library) we use.
// Kept local instead of pulling in @types/google.maps.

export interface GoogleLatLng {
    lat(): number;
    lng(): number;
}

export interface GooglePlace {
    name?: string;
    formatted_address?: string;
    geometry?: { location: GoogleLatLng };
}

export interface GoogleMap {
    setCenter(location: GoogleLatLng): void;
    setZoom(zoom: number): void;
}

export interface GoogleMarker {
    setMap(map: GoogleMap | null): void;
}

export interface GoogleAutocomplete {
    bindTo(key: string, target: GoogleMap): void;
    setFields(fields: string[]): void;
    addListener(eventName: string, handler: () => void): void;
    getPlace(): GooglePlace;
}

export interface GooglePlacesService {
    findPlaceFromQuery(
        request: { query: string; fields: string[] },
        callback: (results: GooglePlace[] | null, status: string) => void
    ): void;
}

export interface GoogleMaps {
    Map: new (container: HTMLElement, options: { center: { lat: number; lng: number }; zoom: number }) => GoogleMap;
    Marker: new (options: { position: GoogleLatLng; map: GoogleMap; animation?: unknown }) => GoogleMarker;
    Animation: { DROP: unknown };
    event: { clearInstanceListeners(instance: unknown): void };
    importLibrary(name: string): Promise<unknown>;
    places: {
        Autocomplete: new (input: HTMLInputElement) => GoogleAutocomplete;
        PlacesService: new (map: GoogleMap) => GooglePlacesService;
        PlacesServiceStatus: { OK: string };
    };
}

declare global {
    interface Window {
        google?: { maps?: GoogleMaps };
        __ungappedGoogleMapsLoaded?: () => void;
    }
}

let loadPromise: Promise<GoogleMaps> | null = null;

// With loading=async the bootstrap only exposes importLibrary; Map/Marker/places must be imported explicitly.
function importLibraries(maps: GoogleMaps) {
    return Promise.all([maps.importLibrary('maps'), maps.importLibrary('marker'), maps.importLibrary('places')]).then(() => maps);
}

/** Loads the Google Maps JS API with the places library once per page. */
export function loadGoogleMaps(apiKey: string): Promise<GoogleMaps> {
    if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
    if (loadPromise) return loadPromise;

    const bootstrapped = window.google?.maps;
    loadPromise = bootstrapped
        ? // The script loaded before but importing a library failed: retry the imports, as a second script would load the API twice.
          importLibraries(bootstrapped)
        : new Promise<GoogleMaps>((resolve, reject) => {
              window.__ungappedGoogleMapsLoaded = () => importLibraries(window.google!.maps!).then(resolve, reject);

              const script = document.createElement('script');
              script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&v=quarterly&callback=__ungappedGoogleMapsLoaded`;
              script.async = true;
              script.onerror = () => {
                  script.remove();
                  reject(new Error('Failed to load Google Maps'));
              };
              document.head.appendChild(script);
          });

    const current = loadPromise;
    current.catch(() => {
        if (loadPromise === current) loadPromise = null;
    });
    return current;
}
